import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GAME_COLORS = {
  magic: '#B8860B', pokemon: '#FFCB05', yugioh: '#7B68EE',
  lorcana: '#4169E1', onepiece: '#DC143C', dbs: '#FF4500',
};
const GAME_LABELS = {
  magic: 'Magic', pokemon: 'Pokémon', yugioh: 'Yu-Gi-Oh!',
  lorcana: 'Lorcana', onepiece: 'One Piece', dbs: 'DBS',
};
const GAME_ICONS = {
  magic: 'M', pokemon: 'P', yugioh: 'Y', lorcana: 'L', onepiece: 'O', dbs: 'D',
};

const getGameColor = (g) => GAME_COLORS[g] || '#888';
const getGameLabel = (g) => GAME_LABELS[g] || g;
const getGameIcon = (g) => GAME_ICONS[g] || '?';

const formatTime = (d) => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
const formatDayNum = (d) => new Date(d).getDate();
const formatDayName = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');
const formatMonthYear = (d) => new Date(d).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
const formatShortDate = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
const formatFullDate = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fillPercent = (count, cap) => cap > 0 ? Math.min(100, Math.round((count / cap) * 100)) : 0;

const embedStyles = `
@keyframes embedFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes embedPulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
@keyframes embedSlideIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
@keyframes embedPopIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
.embed-item { animation: embedFadeIn 0.35s ease both; }
.embed-badge-pulse:hover { animation: embedPulse 0.5s ease; }
.embed-glass { backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); }
.embed-fill-bar { transition: width 0.6s cubic-bezier(0.4,0,0.2,1); }
.embed-card-hover { transition: transform 0.25s ease, box-shadow 0.25s ease; }
.embed-card-hover:hover { transform: translateY(-4px); }
.embed-list-row { transition: background-color 0.2s ease, transform 0.15s ease; }
.embed-list-row:hover { transform: translateX(4px); }
.embed-cal-day { animation: embedSlideIn 0.3s ease both; }
.embed-popup { animation: embedPopIn 0.2s ease; }
.embed-overlay { animation: embedFadeIn 0.15s ease; }
`;

// ─── Game Display Helper ───
function GameBadge({ game, colors, gameLogos, showMode }) {
  const gc = getGameColor(game);
  const logoUrl = gameLogos?.[game];
  if (showMode === 'logo' && logoUrl) {
    return <img src={logoUrl} alt={getGameLabel(game)} style={{ height: 18, objectFit: 'contain', borderRadius: 3 }} />;
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 10, fontWeight: 700, letterSpacing: '0.05em',
      padding: '2px 6px', borderRadius: 4,
      backgroundColor: `${gc}20`, color: gc,
    }}>{getGameLabel(game)}</span>
  );
}

// ─── Register Button ───
function RegisterBtn({ tournament, colors, onRegister, small }) {
  const isFull = tournament.participants_count >= tournament.capacity;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onRegister(tournament); }}
      disabled={isFull}
      style={{
        padding: small ? '3px 8px' : '5px 14px',
        borderRadius: 6, border: 'none', cursor: isFull ? 'default' : 'pointer',
        fontSize: small ? 10 : 11, fontWeight: 600,
        backgroundColor: isFull ? `${colors.text}10` : `${colors.primary}20`,
        color: isFull ? `${colors.text}40` : colors.primary,
        transition: 'background-color 0.2s',
        whiteSpace: 'nowrap',
      }}
      onMouseEnter={(e) => { if (!isFull) e.target.style.backgroundColor = `${colors.primary}35`; }}
      onMouseLeave={(e) => { if (!isFull) e.target.style.backgroundColor = `${colors.primary}20`; }}
      data-testid={`register-btn-${tournament.id}`}
    >
      {isFull ? 'Complet' : "S'inscrire"}
    </button>
  );
}

// ─── Event Popup ───
function EventPopup({ tournament, colors, onClose, onRegister, separatorColor }) {
  if (!tournament) return null;
  const gc = separatorColor === 'game' ? getGameColor(tournament.game) : (separatorColor || getGameColor(tournament.game));
  const pct = fillPercent(tournament.participants_count, tournament.capacity);
  const isFull = pct >= 100;

  return (
    <div className="embed-overlay" style={{
      position: 'fixed', inset: 0, zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.6)',
    }} onClick={onClose}>
      <div className="embed-popup embed-glass" onClick={e => e.stopPropagation()} style={{
        width: '90%', maxWidth: 480, borderRadius: 16, overflow: 'hidden',
        backgroundColor: colors.bg, border: `1px solid ${colors.text}15`,
        boxShadow: `0 24px 48px rgba(0,0,0,0.4)`,
      }}>
        {/* Top accent */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${gc}, ${gc}60)` }} />

        <div style={{ padding: 24 }}>
          {/* Game + Format */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 5, backgroundColor: `${gc}20`, color: gc }}>{getGameLabel(tournament.game)}</span>
            {tournament.format && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, backgroundColor: `${colors.text}08`, color: `${colors.text}50` }}>{tournament.format}</span>}
          </div>

          {/* Title */}
          <h2 style={{ fontSize: 18, fontWeight: 700, color: colors.text, marginBottom: 16, lineHeight: 1.3 }}>{tournament.name}</h2>

          {/* Description */}
          {tournament.description && (
            <p style={{ fontSize: 13, color: `${colors.text}70`, lineHeight: 1.6, marginBottom: 16, whiteSpace: 'pre-line' }}>{tournament.description}</p>
          )}

          {/* Info grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
            <InfoRow icon="calendar" label="Date" value={formatFullDate(tournament.date)} colors={colors} />
            {tournament.venue?.name && <InfoRow icon="pin" label="Lieu" value={`${tournament.venue.name}${tournament.venue.city ? `, ${tournament.venue.city}` : ''}`} colors={colors} />}
            {tournament.venue?.address && <InfoRow icon="map" label="Adresse" value={tournament.venue.address} colors={colors} />}
            <InfoRow icon="euro" label="Prix" value={tournament.price === 0 ? 'Gratuit' : `${tournament.price} €`} colors={colors} />
          </div>

          {/* Fill bar */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: `${colors.text}60`, marginBottom: 6 }}>
              <span>Joueurs inscrits</span>
              <span style={{ fontWeight: 600, color: colors.text }}>{tournament.participants_count}/{tournament.capacity}</span>
            </div>
            <div style={{ height: 6, borderRadius: 3, backgroundColor: `${colors.text}10`, overflow: 'hidden' }}>
              <div className="embed-fill-bar" style={{
                width: `${pct}%`, height: '100%', borderRadius: 3,
                backgroundColor: isFull ? '#EF4444' : pct > 75 ? '#F59E0B' : gc,
              }} />
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => onRegister(tournament)} disabled={isFull} style={{
              flex: 1, padding: '10px 0', borderRadius: 10, border: 'none', cursor: isFull ? 'default' : 'pointer',
              fontSize: 14, fontWeight: 700,
              backgroundColor: isFull ? `${colors.text}10` : colors.primary,
              color: isFull ? `${colors.text}40` : '#fff',
              transition: 'opacity 0.2s',
            }} data-testid={`popup-register-btn-${tournament.id}`}>
              {isFull ? 'Tournoi complet' : "S'inscrire"}
            </button>
            <button onClick={onClose} style={{
              padding: '10px 20px', borderRadius: 10, border: `1px solid ${colors.text}20`,
              backgroundColor: 'transparent', color: `${colors.text}60`, fontSize: 13, fontWeight: 500,
              cursor: 'pointer',
            }}>Fermer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value, colors }) {
  const icons = {
    calendar: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
    pin: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    map: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    euro: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={colors.primary} strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  };
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
      <div style={{ marginTop: 2, flexShrink: 0 }}>{icons[icon]}</div>
      <div>
        <div style={{ fontSize: 10, color: `${colors.text}40`, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
        <div style={{ fontSize: 12, color: `${colors.text}80`, marginTop: 1 }}>{value}</div>
      </div>
    </div>
  );
}

// ─── Calendar Layout (macOS Agenda Style) ───
function CalendarLayout({ tournaments, colors, onEventClick, onRegister, separatorColor, gameLogos, gameDisplay }) {
  const grouped = {};
  tournaments.forEach(t => {
    const key = new Date(t.date).toISOString().slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(t);
  });
  const days = Object.keys(grouped).sort();
  const months = {};
  days.forEach(d => {
    const mk = formatMonthYear(d);
    if (!months[mk]) months[mk] = [];
    months[mk].push(d);
  });

  const getSepColor = (game) => separatorColor === 'game' ? getGameColor(game) : (separatorColor || getGameColor(game));

  return (
    <div style={{ fontFamily: '-apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {Object.entries(months).map(([month, monthDays]) => (
        <div key={month}>
          <div style={{
            padding: '14px 20px 8px', fontSize: 13, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.primary,
            borderBottom: `1px solid ${colors.primary}15`,
            background: `linear-gradient(180deg, ${colors.bg} 0%, ${colors.bg}ee 100%)`,
            position: 'sticky', top: 0, zIndex: 10,
          }}>{month}</div>

          {monthDays.map((dayKey, dayIdx) => {
            const items = grouped[dayKey];
            const isToday = dayKey === new Date().toISOString().slice(0, 10);
            return (
              <div key={dayKey} className="embed-cal-day" style={{ display: 'flex', borderBottom: `1px solid ${colors.text}08`, animationDelay: `${dayIdx * 0.05}s` }}>
                <div style={{
                  width: 72, flexShrink: 0, padding: '16px 0',
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  borderRight: `1px solid ${colors.text}10`,
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', color: isToday ? colors.primary : `${colors.text}60`, letterSpacing: '0.04em' }}>{formatDayName(dayKey)}</span>
                  <span style={{ fontSize: 28, fontWeight: 300, lineHeight: 1.1, color: isToday ? colors.primary : colors.text, marginTop: 2 }}>{formatDayNum(dayKey)}</span>
                  {isToday && <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.primary, marginTop: 4 }} />}
                </div>
                <div style={{ flex: 1, padding: '8px 0' }}>
                  {items.map((t, i) => (
                    <div key={t.id} className="embed-item embed-list-row" style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '10px 16px', borderRadius: 8, margin: '2px 8px',
                      cursor: 'pointer', animationDelay: `${(dayIdx * items.length + i) * 0.03}s`,
                    }} onClick={() => onEventClick(t)} data-testid={`cal-event-${t.id}`}>
                      <div style={{ width: 4, height: 36, borderRadius: 3, backgroundColor: getSepColor(t.game), flexShrink: 0 }} />
                      <div style={{ width: 48, textAlign: 'center', flexShrink: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{formatTime(t.date)}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                          <GameBadge game={t.game} colors={colors} gameLogos={gameLogos} showMode={gameDisplay} />
                          {t.format && <span style={{ fontSize: 11, color: `${colors.text}50` }}>{t.format}</span>}
                          {t.venue?.city && <span style={{ fontSize: 11, color: `${colors.text}40` }}>{t.venue.city}</span>}
                        </div>
                      </div>
                      {/* Right: players + price + register */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: `${colors.text}70` }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline', verticalAlign: '-2px', marginRight: 3 }}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            {t.participants_count}/{t.capacity}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: t.price === 0 ? '#4CAF50' : colors.primary, marginTop: 2 }}>
                            {t.price === 0 ? 'Gratuit' : `${t.price}\u00A0\u20AC`}
                          </div>
                        </div>
                        <RegisterBtn tournament={t} colors={colors} onRegister={onRegister} small />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── Agenda (month grid) Layout ───
function AgendaLayout({ tournaments, colors, onEventClick, separatorColor, gameLogos, gameDisplay }) {
  const [cursor, setCursor] = React.useState(() => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; });
  const [view, setView] = React.useState('month');
  const [selectedDay, setSelectedDay] = React.useState(new Date().toISOString().slice(0, 10));

  const sep = (game) => separatorColor === 'game' ? getGameColor(game) : (separatorColor || getGameColor(game));
  const byDay = {};
  tournaments.forEach((t) => { const k = new Date(t.date).toISOString().slice(0, 10); (byDay[k] = byDay[k] || []).push(t); });

  const monthLabel = cursor.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  const WEEKDAYS = ['LUN', 'MAR', 'MER', 'JEU', 'VEN', 'SAM', 'DIM'];
  const todayKey = new Date().toISOString().slice(0, 10);

  // Build cells for month or week
  const buildMonthCells = () => {
    const first = new Date(cursor);
    const startOffset = (first.getDay() + 6) % 7; // Monday-based
    const gridStart = new Date(first); gridStart.setDate(1 - startOffset);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  };
  const buildWeekCells = () => {
    const base = new Date(selectedDay);
    const off = (base.getDay() + 6) % 7;
    const start = new Date(base); start.setDate(base.getDate() - off);
    return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  };

  const cells = view === 'week' ? buildWeekCells() : buildMonthCells();
  const nav = (dir) => {
    const d = new Date(cursor);
    if (view === 'week') { const s = new Date(selectedDay); s.setDate(s.getDate() + dir * 7); setSelectedDay(s.toISOString().slice(0, 10)); }
    else { d.setMonth(d.getMonth() + dir); setCursor(d); }
  };
  const goToday = () => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); setCursor(d); setSelectedDay(new Date().toISOString().slice(0, 10)); };

  const dayEvents = byDay[selectedDay] || [];
  const btn = (label, val) => (
    <button onClick={() => setView(val)} style={{
      padding: '6px 14px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
      background: view === val ? colors.primary : 'transparent', color: view === val ? '#fff' : `${colors.text}80`,
    }} data-testid={`agenda-view-${val}`}>{label}</button>
  );

  const renderCell = (d) => {
    const key = d.toISOString().slice(0, 10);
    const inMonth = d.getMonth() === cursor.getMonth() || view === 'week';
    const items = byDay[key] || [];
    const isToday = key === todayKey;
    const isSel = key === selectedDay;
    return (
      <div key={key} onClick={() => setSelectedDay(key)} style={{
        minHeight: view === 'week' ? 120 : 92, padding: 6, cursor: 'pointer',
        background: isSel ? `${colors.primary}18` : 'transparent',
        border: `1px solid ${colors.text}12`, borderRadius: 8, opacity: inMonth ? 1 : 0.35,
        outline: isToday ? `1.5px solid ${colors.primary}` : 'none',
      }} data-testid={`agenda-cell-${key}`}>
        <div style={{ fontSize: 12, fontWeight: 700, color: isToday ? colors.primary : colors.text, marginBottom: 4 }}>{d.getDate()}</div>
        {items.slice(0, 3).map((t) => (
          <div key={t.id} onClick={(e) => { e.stopPropagation(); onEventClick(t); }} style={{
            display: 'flex', alignItems: 'center', gap: 4, background: `${sep(t.game)}22`, color: sep(t.game),
            borderRadius: 5, padding: '2px 5px', marginBottom: 2, fontSize: 10, fontWeight: 600,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }} data-testid={`agenda-event-${t.id}`}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: sep(t.game), flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
          </div>
        ))}
        {items.length > 3 && <div style={{ fontSize: 9, color: `${colors.text}60` }}>+{items.length - 3}</div>}
      </div>
    );
  };

  return (
    <div style={{ padding: '16px 12px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }} data-testid="agenda-layout">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => nav(-1)} style={{ width: 34, height: 34, borderRadius: '50%', border: `1px solid ${colors.text}20`, background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: 16 }} data-testid="agenda-prev">‹</button>
          <span style={{ fontSize: 17, fontWeight: 700, color: colors.text, textTransform: 'capitalize', minWidth: 150, textAlign: 'center' }} data-testid="agenda-month-label">{monthLabel}</span>
          <button onClick={() => nav(1)} style={{ width: 34, height: 34, borderRadius: '50%', border: `1px solid ${colors.text}20`, background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: 16 }} data-testid="agenda-next">›</button>
          <button onClick={goToday} style={{ padding: '6px 12px', borderRadius: 20, border: `1px solid ${colors.primary}`, background: 'transparent', color: colors.primary, cursor: 'pointer', fontSize: 12, fontWeight: 600 }} data-testid="agenda-today">Aujourd'hui</button>
        </div>
        <div style={{ display: 'flex', gap: 4, background: `${colors.text}10`, borderRadius: 20, padding: 3 }}>
          {btn('Mois', 'month')}{btn('Semaine', 'week')}{btn('Jour', 'day')}
        </div>
      </div>

      {view !== 'day' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 6 }}>
            {WEEKDAYS.map((w) => <div key={w} style={{ textAlign: 'center', fontSize: 11, fontWeight: 700, color: `${colors.text}55`, letterSpacing: '0.05em' }}>{w}</div>)}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
            {cells.map(renderCell)}
          </div>
        </>
      )}

      {/* Day detail */}
      <div style={{ marginTop: 18, border: `1px solid ${colors.text}12`, borderRadius: 12, padding: 16 }} data-testid="agenda-day-detail">
        <div style={{ fontSize: 13, fontWeight: 700, color: colors.primary, marginBottom: 12 }}>
          {new Date(selectedDay).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
        {dayEvents.length === 0 ? (
          <div style={{ fontSize: 13, color: `${colors.text}55`, textAlign: 'center', padding: 12 }}>Aucun tournoi ce jour</div>
        ) : dayEvents.map((t) => (
          <div key={t.id} onClick={() => onEventClick(t)} style={{ display: 'flex', gap: 12, padding: 10, borderRadius: 10, cursor: 'pointer', marginBottom: 6, background: `${colors.text}06` }} data-testid={`agenda-detail-${t.id}`}>
            <div style={{ width: 4, borderRadius: 3, background: sep(t.game), flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{t.name}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <GameBadge game={t.game} colors={colors} gameLogos={gameLogos} showMode={gameDisplay} />
                <span style={{ fontSize: 12, color: `${colors.text}70` }}>🕐 {formatTime(t.date)}</span>
                {t.format && <span style={{ fontSize: 12, color: `${colors.text}50` }}>{t.format}</span>}
                <span style={{ fontSize: 12, fontWeight: 600, color: t.price === 0 ? '#4CAF50' : colors.primary }}>{t.price === 0 ? 'Gratuit' : `${t.price}\u00A0\u20AC`}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── List Layout ───
function ListLayout({ tournaments, colors, onEventClick, onRegister, separatorColor, gameLogos, gameDisplay }) {
  const getSepColor = (game) => separatorColor === 'game' ? getGameColor(game) : (separatorColor || getGameColor(game));
  return (
    <div style={{ padding: '4px 0' }}>
      {tournaments.map((t, i) => {
        const pct = fillPercent(t.participants_count, t.capacity);
        const isFull = pct >= 100;
        return (
          <div key={t.id} className="embed-item embed-list-row" style={{
            display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px',
            borderBottom: `1px solid ${colors.text}08`, cursor: 'pointer', animationDelay: `${i * 0.04}s`,
          }} onClick={() => onEventClick(t)} data-testid={`list-event-${t.id}`}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: `${getSepColor(t.game)}18`, border: `1.5px solid ${getSepColor(t.game)}30`,
              color: getSepColor(t.game), fontSize: 15, fontWeight: 800,
            }}>{getGameIcon(t.game)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>{t.name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 5, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 11, color: `${colors.text}60`, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {formatShortDate(t.date)} {formatTime(t.date)}
                </span>
                {t.format && <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4, backgroundColor: `${colors.text}10`, color: `${colors.text}50` }}>{t.format}</span>}
                {t.venue?.city && <span style={{ fontSize: 11, color: `${colors.text}40`, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {t.venue.city}</span>}
              </div>
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: `${colors.text}10`, overflow: 'hidden', maxWidth: 120 }}>
                  <div className="embed-fill-bar" style={{ width: `${pct}%`, height: '100%', borderRadius: 2, backgroundColor: isFull ? '#EF4444' : pct > 75 ? '#F59E0B' : getSepColor(t.game) }} />
                </div>
                <span style={{ fontSize: 10, color: `${colors.text}45`, fontWeight: 500 }}>{t.participants_count}/{t.capacity}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <div className="embed-badge-pulse" style={{
                padding: '5px 12px', borderRadius: 20,
                fontSize: 12, fontWeight: 700,
                backgroundColor: t.price === 0 ? '#4CAF5018' : `${colors.primary}15`,
                color: t.price === 0 ? '#4CAF50' : colors.primary,
                border: `1px solid ${t.price === 0 ? '#4CAF5025' : `${colors.primary}25`}`,
              }}>{t.price === 0 ? 'Gratuit' : `${t.price}\u00A0\u20AC`}</div>
              <RegisterBtn tournament={t} colors={colors} onRegister={onRegister} small />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Cards Layout ───
function CardsLayout({ tournaments, colors, cols, onEventClick, onRegister, separatorColor, gameLogos, gameDisplay }) {
  const getSepColor = (game) => separatorColor === 'game' ? getGameColor(game) : (separatorColor || getGameColor(game));
  return (
    <div style={{ display: 'grid', gap: 16, padding: 16, gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
      {tournaments.map((t, i) => {
        const pct = fillPercent(t.participants_count, t.capacity);
        const gc = getSepColor(t.game);
        const isFull = pct >= 100;
        return (
          <div key={t.id} className="embed-item embed-card-hover embed-glass" style={{
            borderRadius: 14, padding: 18, position: 'relative', overflow: 'hidden',
            backgroundColor: `${colors.text}06`, border: `1px solid ${colors.text}10`,
            boxShadow: `0 4px 24px ${gc}08, 0 1px 3px rgba(0,0,0,0.12)`,
            cursor: 'pointer', animationDelay: `${i * 0.05}s`,
          }} onClick={() => onEventClick(t)} data-testid={`card-event-${t.id}`}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${gc}, ${gc}60)` }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="embed-badge-pulse" style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: 8, backgroundColor: `${gc}20`, color: gc,
                  fontSize: 13, fontWeight: 800,
                }}>{getGameIcon(t.game)}</span>
                <GameBadge game={t.game} colors={colors} gameLogos={gameLogos} showMode={gameDisplay} />
              </div>
              {t.format && <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6, backgroundColor: `${colors.text}08`, color: `${colors.text}50` }}>{t.format}</span>}
            </div>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.text, lineHeight: 1.35, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{t.name}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: `${colors.text}55` }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                {formatShortDate(t.date)} - {formatTime(t.date)}
              </div>
              {(t.venue?.name || t.venue?.city) && <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: `${colors.text}45` }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                {t.venue.name || t.venue.city}
              </div>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: t.price === 0 ? '#4CAF50' : colors.primary }}>{t.price === 0 ? 'Gratuit' : `${t.price}\u00A0\u20AC`}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: `${colors.text}50`, fontWeight: 500 }}>{t.participants_count}/{t.capacity}</span>
                <div style={{ width: 48, height: 4, borderRadius: 2, backgroundColor: `${colors.text}10`, overflow: 'hidden' }}>
                  <div className="embed-fill-bar" style={{ width: `${pct}%`, height: '100%', borderRadius: 2, backgroundColor: isFull ? '#EF4444' : pct > 75 ? '#F59E0B' : gc }} />
                </div>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <RegisterBtn tournament={t} colors={colors} onRegister={onRegister} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Planin Layout ───
function PlaninLayout({ tournaments, colors, onEventClick, onRegister }) {
  // Group by date
  const grouped = {};
  tournaments.forEach(t => {
    const key = new Date(t.date).toISOString().slice(0, 10);
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(t);
  });
  const days = Object.keys(grouped).sort();

  const formatDayHeader = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  };
  const formatHour = (d) => new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  const getEndTime = (t) => {
    if (t.end_date) return formatHour(t.end_date);
    // Default: +3h
    const d = new Date(t.date);
    d.setHours(d.getHours() + 3);
    return formatHour(d);
  };

  const now = new Date().toISOString();

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      {days.map((dayKey, dayIdx) => {
        const items = grouped[dayKey];
        return (
          <div key={dayKey} className="embed-item" style={{ animationDelay: `${dayIdx * 0.05}s` }}>
            {/* Date header bar */}
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
              borderTop: dayIdx > 0 ? '1px solid #e0e0e0' : 'none',
            }}>
              <span style={{
                fontSize: 16, fontWeight: 700, color: '#333',
                textTransform: 'capitalize',
              }}>{formatDayHeader(dayKey)}</span>
            </div>

            {/* Events */}
            {items.map((t, i) => {
              const placesLeft = Math.max(0, t.capacity - t.participants_count);
              const isFull = placesLeft === 0;
              const isInProgress = t.date < now && (t.end_date ? t.end_date > now : true);
              const gc = getGameColor(t.game);

              return (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'stretch',
                  borderBottom: '1px solid #eee',
                  minHeight: 80,
                  transition: 'background-color 0.15s',
                  cursor: 'default',
                }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fafafa'}
                   onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>

                  {/* Col 1: Time */}
                  <div style={{
                    width: 130, flexShrink: 0, padding: '14px 16px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    borderRight: '1px solid #eee',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>De</span>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>{formatHour(t.date)}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 2 }}>
                      <span style={{ fontSize: 12, color: '#999', fontWeight: 400 }}>à</span>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#333' }}>{getEndTime(t)}</span>
                    </div>
                  </div>

                  {/* Col 2: Game + Format + Description link */}
                  <div style={{
                    flex: 1, padding: '14px 20px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    borderRight: '1px solid #eee',
                    minWidth: 0,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: 6,
                        backgroundColor: `${gc}20`, color: gc,
                        fontSize: 11, fontWeight: 800, flexShrink: 0,
                      }}>{getGameIcon(t.game)}</span>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#333' }}>
                        {getGameLabel(t.game)}{t.name !== getGameLabel(t.game) ? ` : ${t.name}` : ''}
                      </span>
                    </div>
                    {t.format && <div style={{ fontSize: 13, color: '#888', marginTop: 3, marginLeft: 32 }}>{t.format}</div>}
                    <div style={{ marginTop: 6, marginLeft: 32 }}>
                      <button onClick={() => onEventClick(t)} style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        fontSize: 12, color: '#00897B', fontWeight: 500,
                        textDecoration: 'none',
                      }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                         onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                         data-testid={`planin-desc-${t.id}`}>
                        Voir la description
                      </button>
                    </div>
                  </div>

                  {/* Col 3: Price + places */}
                  <div style={{
                    width: 150, flexShrink: 0, padding: '14px 16px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    alignItems: 'center', borderRight: '1px solid #eee',
                  }}>
                    <span style={{
                      fontSize: 20, fontWeight: 700,
                      color: t.price === 0 ? '#00897B' : '#00897B',
                    }}>{t.price === 0 ? 'Gratuit' : `${t.price.toFixed(2).replace('.', ',')} \u20AC`}</span>
                    {!isFull && !isInProgress && (
                      <span style={{ fontSize: 12, color: '#4CAF50', fontWeight: 500, marginTop: 4 }}>
                        {placesLeft} place{placesLeft > 1 ? 's' : ''} restante{placesLeft > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Col 4: Status + fiche link */}
                  <div style={{
                    width: 180, flexShrink: 0, padding: '14px 16px',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                    alignItems: 'center', gap: 8,
                  }}>
                    {isInProgress ? (
                      <span style={{
                        padding: '6px 16px', borderRadius: 20,
                        fontSize: 13, fontWeight: 500,
                        backgroundColor: '#f0f0f0', color: '#666',
                      }}>En cours</span>
                    ) : isFull ? (
                      <span style={{
                        padding: '6px 16px', borderRadius: 20,
                        fontSize: 13, fontWeight: 500,
                        backgroundColor: '#f0f0f0', color: '#999',
                      }}>Complet</span>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); onRegister(t); }} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '7px 18px', borderRadius: 20,
                        fontSize: 13, fontWeight: 600,
                        backgroundColor: '#5C4EBF', color: '#fff',
                        border: 'none', cursor: 'pointer',
                        transition: 'background-color 0.2s',
                      }} onMouseEnter={(e) => e.target.style.backgroundColor = '#4A3EA0'}
                         onMouseLeave={(e) => e.target.style.backgroundColor = '#5C4EBF'}
                         data-testid={`planin-register-${t.id}`}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                        S'inscrire
                      </button>
                    )}
                    <button onClick={() => onEventClick(t)} style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontSize: 12, color: '#00897B', fontWeight: 500,
                    }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                       onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                       data-testid={`planin-fiche-${t.id}`}>
                      Voir la fiche de l'événement
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ─── Week helpers ───
const getMonday = (d) => {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  const day = (date.getDay() + 6) % 7; // Mon=0 ... Sun=6
  date.setDate(date.getDate() - day);
  return date;
};
const getISOWeek = (d) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
  return 1 + Math.round((date - firstThursday) / (7 * 24 * 3600 * 1000));
};
const weekDayShort = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');
const dayMonthShort = (d) => `${new Date(d).getDate()}.${new Date(d).getMonth() + 1}`;
const eventEndTime = (t) => {
  if (t.end_date) return formatTime(t.end_date);
  const d = new Date(t.date);
  d.setHours(d.getHours() + 3);
  return formatTime(d);
};

// ─── Week Layout (Atmos Arena Style) ───
function WeekLayout({ tournaments, colors, onEventClick, onRegister, separatorColor, gameLogos, gameDisplay }) {
  const [weekOffset, setWeekOffset] = useState(0);
  const currentMonday = getMonday(new Date());
  const weekStart = new Date(currentMonday);
  weekStart.setDate(weekStart.getDate() + weekOffset * 7);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const getSepColor = (game) => separatorColor === 'game' ? getGameColor(game) : (separatorColor || getGameColor(game));

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    days.push(d);
  }
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  const eventsForDay = (day) => tournaments.filter(t => sameDay(new Date(t.date), day)).sort((a, b) => new Date(a.date) - new Date(b.date));
  const today = new Date();

  const weekNum = getISOWeek(weekStart);
  const canGoPrev = weekOffset > 0;

  return (
    <div>
      {/* Week navigation header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, padding: '14px 20px', flexWrap: 'wrap',
        borderBottom: `1px solid ${colors.text}12`,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: colors.text }}>
          Événements à venir
          <span style={{ color: colors.primary, marginLeft: 8 }}>
            S {weekNum}/{weekStart.getFullYear()}
          </span>
          <span style={{ color: `${colors.text}70`, fontWeight: 500, marginLeft: 8, fontSize: 13 }}>
            ({dayMonthShort(weekStart)} – {dayMonthShort(weekEnd)})
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => canGoPrev && setWeekOffset(weekOffset - 1)}
            disabled={!canGoPrev}
            data-testid="week-prev-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: `1px solid ${colors.text}15`,
              backgroundColor: 'transparent',
              color: canGoPrev ? colors.text : `${colors.text}25`,
              cursor: canGoPrev ? 'pointer' : 'not-allowed',
              opacity: canGoPrev ? 1 : 0.5,
              transition: 'background-color 0.2s',
            }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
            Précédente
          </button>
          <button
            onClick={() => setWeekOffset(weekOffset + 1)}
            data-testid="week-next-btn"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
              border: 'none', backgroundColor: colors.primary, color: '#fff',
              cursor: 'pointer', transition: 'opacity 0.2s',
            }}>
            Suivante
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>
      </div>

      {/* Days */}
      <div>
        {days.map((day, dayIdx) => {
          const items = eventsForDay(day);
          const isToday = sameDay(day, today);
          return (
            <div key={dayIdx} className="embed-cal-day" style={{ animationDelay: `${dayIdx * 0.04}s` }}>
              {/* Day header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 20px',
                backgroundColor: isToday ? `${colors.primary}12` : `${colors.text}05`,
                borderBottom: `1px solid ${colors.text}0a`,
              }}>
                {isToday && <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.primary }} />}
                <span style={{
                  fontSize: 13, fontWeight: 700, textTransform: 'capitalize',
                  color: isToday ? colors.primary : colors.text,
                }}>
                  {weekDayShort(day)}, {day.getDate()}.{day.getMonth() + 1}.
                </span>
              </div>

              {/* Events */}
              {items.length === 0 ? (
                <div style={{ padding: '10px 20px', fontSize: 12, color: `${colors.text}35` }}>
                  Aucun événement
                </div>
              ) : (
                items.map((t) => {
                  const isFull = t.participants_count >= t.capacity;
                  return (
                    <div key={t.id} className="embed-item embed-list-row" style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 20px', cursor: 'pointer',
                      borderBottom: `1px solid ${colors.text}08`,
                    }} onClick={() => onEventClick(t)} data-testid={`week-event-${t.id}`}>
                      <div style={{ width: 4, alignSelf: 'stretch', minHeight: 32, borderRadius: 3, backgroundColor: getSepColor(t.game), flexShrink: 0 }} />
                      <div style={{ width: 110, flexShrink: 0, fontSize: 13, fontWeight: 600, color: colors.text }}>
                        {formatTime(t.date)}<span style={{ color: `${colors.text}45`, fontWeight: 400 }}> – {eventEndTime(t)}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 500, color: colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                          <GameBadge game={t.game} colors={colors} gameLogos={gameLogos} showMode={gameDisplay} />
                          {t.format && <span style={{ fontSize: 11, color: `${colors.text}50` }}>{t.format}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: t.price === 0 ? '#4CAF50' : colors.primary }}>
                          {t.price === 0 ? 'Gratuit' : `${t.price}\u00A0\u20AC`}
                        </span>
                        <RegisterBtn tournament={t} colors={colors} onRegister={onRegister} small />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Main Embed Page ───
export default function EmbedPage() {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('date');
  const [filterGame, setFilterGame] = useState('');
  const [filterFormat, setFilterFormat] = useState('');
  const [hideFull, setHideFull] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [allGames, setAllGames] = useState([]);
  const [allTournaments, setAllTournaments] = useState([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [apiConfig, setApiConfig] = useState(null);

  // Short mode: ?l=planin (fetch config from API)
  const shortLayout = searchParams.get('l');
  // Legacy mode: ?layout=planin&primary=...&bg=... (inline params)
  const inlineLayout = searchParams.get('layout');

  const layout = shortLayout || inlineLayout || 'calendar';

  // If short mode, fetch config from API
  useEffect(() => {
    if (shortLayout) {
      axios.get(`${API_URL}/api/embed/${orgId}/config`, { params: { layout: shortLayout } })
        .then(r => { setApiConfig(r.data); setConfigLoaded(true); })
        .catch(() => setConfigLoaded(true));
    } else {
      setConfigLoaded(true);
    }
  }, [orgId, shortLayout]);

  // Determine colors from API config or URL params
  const cfg = apiConfig?.layout || {};
  const isLightLayout = layout === 'planin' || layout === 'week';
  const defPrimary = isLightLayout ? '#5C4EBF' : '#FF6B35';
  const defBg = isLightLayout ? '#ffffff' : '#1a1a1a';
  const defText = isLightLayout ? '#333333' : '#ffffff';
  const primary = shortLayout ? (cfg.primary_color || defPrimary) : (searchParams.get('primary') || defPrimary);
  const bg = shortLayout ? (cfg.bg_color || defBg) : (searchParams.get('bg') || defBg);
  const text = shortLayout ? (cfg.text_color || defText) : (searchParams.get('text') || defText);
  const logo = shortLayout ? (cfg.logo_url || '') : (searchParams.get('logo') || '');
  const cols = shortLayout ? (cfg.cards_per_row || 3) : (parseInt(searchParams.get('cols')) || 3);
  const sepColor = shortLayout ? (cfg.separator_color || 'game') : (searchParams.get('sep') || 'game');
  const gameDisplay = shortLayout ? (cfg.game_display || 'name') : (searchParams.get('gd') || 'name');

  const colors = { primary, bg, text };

  // Parse game logos from API config or URL params
  const gameLogos = {};
  if (shortLayout && apiConfig?.game_logos) {
    Object.assign(gameLogos, apiConfig.game_logos);
  } else {
    for (const [key, val] of searchParams.entries()) {
      if (key.startsWith('gl_')) gameLogos[key.slice(3)] = val;
    }
  }

  // Fetch all games on first load (unfiltered), then filtered data
  useEffect(() => {
    axios.get(`${API_URL}/api/embed/${orgId}/tournaments`, { params: { sort: 'date' } })
      .then(r => {
        const all = r.data?.tournaments || [];
        setAllGames([...new Set(all.map(t => t.game))]);
        setAllTournaments(all);
      })
      .catch(() => {});
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    const effectiveSort = layout === 'planin' ? 'date' : sortBy;
    axios.get(`${API_URL}/api/embed/${orgId}/tournaments`, { params: { sort: effectiveSort, game: filterGame || undefined } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgId, sortBy, filterGame, layout]);

  const handleRegister = (tournament) => {
    // Open in new tab for registration (since embed is on external site)
    window.open(`${API_URL?.replace('/api', '')}/events/tournament/${tournament.id}`, '_blank');
  };

  if (loading) return (
    <div style={{ backgroundColor: bg, color: text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ border: `2px solid ${primary}`, borderTopColor: 'transparent', borderRadius: '50%', width: 28, height: 28, animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const rawTournaments = data?.tournaments || [];
  // Apply client-side filters
  let tournaments = rawTournaments;
  if (filterFormat) {
    tournaments = tournaments.filter(t => t.format === filterFormat);
  }
  if (hideFull) {
    tournaments = tournaments.filter(t => t.participants_count < t.capacity);
  }

  // Compute available formats for the selected game
  const availableFormats = filterGame
    ? [...new Set(allTournaments.filter(t => t.game === filterGame).map(t => t.format).filter(Boolean))]
    : [];

  // Reset format when game changes
  const handleGameChange = (game) => {
    setFilterGame(game);
    setFilterFormat('');
  };

  const isPlanin = layout === 'planin';

  return (
    <div style={{
      backgroundColor: isPlanin ? '#ffffff' : bg,
      color: isPlanin ? '#333' : text,
      minHeight: '100vh',
      fontFamily: '-apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 14, lineHeight: 1.5,
    }}>
      <style>{embedStyles}</style>

      {/* Header */}
      {isPlanin ? (
        /* Planin header: game → format correlated + hide full toggle */
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          padding: '12px 20px', gap: 10,
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky', top: 0, zIndex: 20,
          backgroundColor: '#fff',
          flexWrap: 'wrap',
        }}>
          {logo && <img src={logo} alt="" style={{ height: 28, borderRadius: 4, objectFit: 'contain', marginRight: 'auto' }} />}

          {allGames.length > 0 && (
            <select value={filterGame} onChange={(e) => handleGameChange(e.target.value)}
              style={{ backgroundColor: '#f5f5f5', border: '1px solid #ddd', color: '#333', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 500, outline: 'none', cursor: 'pointer' }}
              data-testid="planin-game-filter">
              <option value="">Tous les jeux</option>
              {allGames.map(g => <option key={g} value={g}>{getGameLabel(g)}</option>)}
            </select>
          )}

          {filterGame && availableFormats.length > 0 && (
            <select value={filterFormat} onChange={(e) => setFilterFormat(e.target.value)}
              style={{ backgroundColor: '#f5f5f5', border: '1px solid #ddd', color: '#333', borderRadius: 6, padding: '6px 12px', fontSize: 13, fontWeight: 500, outline: 'none', cursor: 'pointer' }}
              data-testid="planin-format-filter">
              <option value="">Tous les formats</option>
              {availableFormats.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          )}

          <label style={{
            display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
            fontSize: 13, color: '#666', fontWeight: 500,
            padding: '6px 12px', borderRadius: 6,
            backgroundColor: hideFull ? '#E8F5E9' : '#f5f5f5',
            border: `1px solid ${hideFull ? '#81C784' : '#ddd'}`,
            transition: 'all 0.2s',
          }} data-testid="planin-hide-full-toggle">
            <input type="checkbox" checked={hideFull} onChange={(e) => setHideFull(e.target.checked)}
              style={{ accentColor: '#4CAF50', width: 14, height: 14 }} />
            <span>Non complets uniquement</span>
          </label>
        </div>
      ) : (
        /* Other layouts header */
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          padding: '10px 20px', gap: 8,
          borderBottom: `1px solid ${text}08`,
          position: 'sticky', top: 0, zIndex: 20, backgroundColor: bg,
        }}>
          {logo && <img src={logo} alt="" style={{ height: 24, borderRadius: 4, objectFit: 'contain', marginRight: 'auto' }} />}
          {allGames.length > 1 && (
            <select value={filterGame} onChange={(e) => handleGameChange(e.target.value)}
              style={{ backgroundColor: `${primary}10`, border: `1px solid ${primary}20`, color: text, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 500, outline: 'none', cursor: 'pointer' }}
              data-testid="embed-game-filter">
              <option value="">Tous les jeux</option>
              {allGames.map(g => <option key={g} value={g}>{getGameLabel(g)}</option>)}
            </select>
          )}
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
            style={{ backgroundColor: `${primary}10`, border: `1px solid ${primary}20`, color: text, borderRadius: 8, padding: '5px 10px', fontSize: 12, fontWeight: 500, outline: 'none', cursor: 'pointer' }}
            data-testid="embed-sort">
            <option value="date">Date</option>
            <option value="game">Jeu</option>
            <option value="format">Format</option>
          </select>
        </div>
      )}

      {/* Content */}
      {layout === 'agenda' ? (
        <AgendaLayout tournaments={tournaments} colors={colors} onEventClick={setSelectedEvent} separatorColor={sepColor} gameLogos={gameLogos} gameDisplay={gameDisplay} />
      ) : tournaments.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', opacity: 0.4, color: isPlanin ? '#999' : text }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block', opacity: 0.5 }}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          <span style={{ fontSize: 14 }}>Aucun tournoi à venir</span>
        </div>
      ) : layout === 'planin' ? (
        <PlaninLayout tournaments={tournaments} colors={colors} onEventClick={setSelectedEvent} onRegister={handleRegister} />
      ) : layout === 'week' ? (
        <WeekLayout tournaments={tournaments} colors={colors} onEventClick={setSelectedEvent} onRegister={handleRegister} separatorColor={sepColor} gameLogos={gameLogos} gameDisplay={gameDisplay} />
      ) : layout === 'calendar' ? (
        <CalendarLayout tournaments={tournaments} colors={colors} onEventClick={setSelectedEvent} onRegister={handleRegister} separatorColor={sepColor} gameLogos={gameLogos} gameDisplay={gameDisplay} />
      ) : layout === 'list' ? (
        <ListLayout tournaments={tournaments} colors={colors} onEventClick={setSelectedEvent} onRegister={handleRegister} separatorColor={sepColor} gameLogos={gameLogos} gameDisplay={gameDisplay} />
      ) : (
        <CardsLayout tournaments={tournaments} colors={colors} cols={cols} onEventClick={setSelectedEvent} onRegister={handleRegister} separatorColor={sepColor} gameLogos={gameLogos} gameDisplay={gameDisplay} />
      )}

      {/* Popup */}
      {selectedEvent && (
        <EventPopup
          tournament={selectedEvent}
          colors={isPlanin ? { primary: '#5C4EBF', bg: '#ffffff', text: '#333333' } : colors}
          onClose={() => setSelectedEvent(null)}
          onRegister={handleRegister}
          separatorColor={sepColor}
        />
      )}

      <div style={{ textAlign: 'center', padding: '12px 0 8px', opacity: 0.2, fontSize: 10, fontWeight: 500, letterSpacing: '0.04em' }}>
        Powered by Arcanapp
      </div>
    </div>
  );
}
